import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../Modal';
import { PasswordInput } from './PasswordInput';
import { ScanLoginPanel } from './ScanLoginPanel';
import { SmsCodeButton } from './SmsCodeButton';
import { useAuth } from '../../contexts/AuthContext';
import { PRIVACY_POLICY_VERSION } from '../../config/policy';
import type { AuthModalMode } from '../../contexts/AuthModalContext';

interface AuthModalProps {
  open: boolean;
  mode: AuthModalMode;
  onClose: () => void;
  onModeChange: (mode: AuthModalMode) => void;
  onSuccess: () => void;
}

type LoginTab = 'code' | 'password';

export function AuthModal({ open, mode, onClose, onModeChange, onSuccess }: AuthModalProps) {
  const { loginWithCredentials, registerWithCredentials, registerWithPhone, loginWithPhone, phoneVerifyEnabled } =
    useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const [loginTab, setLoginTab] = useState<LoginTab>('code');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // 扫码登录仅桌面(>=lg)展示;移动端不挂载,避免隐藏面板空转出码+轮询。
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsDesktop(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setPassword('');
      setPhone('');
      setCode('');
      setAgreed(false);
      setShowOptional(false);
      setLoginTab('code');
      setError(null);
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    setError(null);
  }, [mode]);

  useEffect(() => {
    setError(null);
  }, [loginTab]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // 手机强制验证总开关关时：回退旧的邮箱密码流程（无手机、无隐私勾选 gate）。
    if (!phoneVerifyEnabled) {
      setLoading(true);
      try {
        if (mode === 'login') {
          await loginWithCredentials(email, password);
        } else {
          await registerWithCredentials({ email, password });
        }
        onSuccess();
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const msg = (err as any)?.response?.data?.message ??
          (mode === 'login' ? '登录失败，请检查邮箱与密码' : '注册失败');
        setError(msg);
      } finally {
        setLoading(false);
      }
      return;
    }
    if (mode === 'register' && !agreed) {
      setError('请阅读并勾选接受《隐私政策》');
      return;
    }
    setLoading(true);
    try {
      if (mode === 'login') {
        if (loginTab === 'code') {
          await loginWithPhone({ phone, code });
        } else {
          await loginWithCredentials(email, password);
        }
      } else {
        await registerWithPhone({
          phone,
          code,
          password: password || undefined,
          email: email || undefined,
          acceptPrivacy: agreed,
          privacyVersion: PRIVACY_POLICY_VERSION,
        });
      }
      onSuccess();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (err as any)?.response?.data?.message ??
        (mode === 'login' ? '登录失败，请稍后重试' : '注册失败');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  const isLogin = mode === 'login';

  const errorBlock = error && (
    <div className="rounded bg-danger-tint p-3 text-sm text-danger max-lg:rounded-chip">{error}</div>
  );

  // ── 手机强制验证总开关关（默认）：回退到旧的邮箱 + 密码注册/登录 UI ──────────────
  if (!phoneVerifyEnabled) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={isLogin ? '登录' : '创建账号'}
        description={
          isLogin
            ? '使用注册邮箱登录知行经管数据。'
            : '注册免费账号，开始浏览与下载经管数据。'
        }
        size={isLogin ? '3xl' : 'md'}
      >
        <div className={isLogin ? 'lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-6' : ''}>
          <div>
            <form className="space-y-4" onSubmit={submit}>
              <div>
                <label className="block text-sm font-medium text-fg-muted">邮箱</label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded border border-line px-3 py-2 text-sm focus:border-navy focus:outline-none"
                />
              </div>
              <PasswordInput
                value={password}
                onChange={setPassword}
                required
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                showStrength={!isLogin}
                label={isLogin ? '密码' : '密码（至少 8 位，含字母与数字）'}
              />
              {errorBlock}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded bg-navy-fill px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-fill-strong disabled:opacity-50 max-lg:rounded-control"
              >
                {loading ? (isLogin ? '登录中…' : '注册中…') : isLogin ? '登录' : '注册'}
              </button>
              {!isLogin && (
                <p className="text-center text-xs leading-relaxed text-fg-faint">
                  为防范盗版倒卖与保障账户安全，本站会记录注册及下载时的设备特征与网络信息。
                </p>
              )}
            </form>
            <div className="mt-4 flex items-center justify-between text-sm text-fg-muted">
              {isLogin ? (
                <>
                  <span>
                    还没有账号？{' '}
                    <button
                      type="button"
                      onClick={() => onModeChange('register')}
                      className="text-navy hover:underline"
                    >
                      注册
                    </button>
                  </span>
                  <Link to="/forgot-password" onClick={onClose} className="text-navy hover:underline">
                    忘记密码？
                  </Link>
                </>
              ) : (
                <span>
                  已有账号？{' '}
                  <button
                    type="button"
                    onClick={() => onModeChange('login')}
                    className="text-navy hover:underline"
                  >
                    登录
                  </button>
                </span>
              )}
            </div>
          </div>

          {isLogin && isDesktop && (
            <>
              <div className="w-px self-stretch bg-line" aria-hidden="true" />
              <div>
                <ScanLoginPanel active={open && isLogin && isDesktop} onSuccess={onSuccess} />
              </div>
            </>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isLogin ? '登录' : '创建账号'}
      description={
        isLogin
          ? '登录知行经管数据，浏览与下载经管数据集。'
          : '使用手机号快速注册，开始浏览与下载经管数据。'
      }
      size={isLogin ? '3xl' : 'md'}
    >
      {isLogin ? (
        <div className="lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-6">
          <div>
            {/* 登录方式切换：验证码登录 / 密码登录 */}
            <div className="mb-4 grid grid-cols-2 gap-1 rounded border border-line p-1 max-lg:rounded-control" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={loginTab === 'code'}
                onClick={() => setLoginTab('code')}
                className={`rounded px-3 py-1.5 text-sm font-medium transition-colors max-lg:rounded-control ${
                  loginTab === 'code'
                    ? 'bg-navy-fill text-white'
                    : 'text-fg-muted hover:text-fg'
                }`}
              >
                验证码登录
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={loginTab === 'password'}
                onClick={() => setLoginTab('password')}
                className={`rounded px-3 py-1.5 text-sm font-medium transition-colors max-lg:rounded-control ${
                  loginTab === 'password'
                    ? 'bg-navy-fill text-white'
                    : 'text-fg-muted hover:text-fg'
                }`}
              >
                密码登录
              </button>
            </div>

            <form className="space-y-4" onSubmit={submit}>
              {loginTab === 'code' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-fg-muted">手机号</label>
                    <input
                      type="tel"
                      required
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="mt-1 w-full rounded border border-line px-3 py-2 text-sm focus:border-navy focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-fg-muted">验证码</label>
                    <div className="mt-1 flex gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        required
                        autoComplete="one-time-code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="6 位验证码"
                        className="w-full rounded border border-line px-3 py-2 text-sm focus:border-navy focus:outline-none"
                      />
                      <SmsCodeButton phone={phone} purpose="login" onError={setError} />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-fg-muted">邮箱</label>
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 w-full rounded border border-line px-3 py-2 text-sm focus:border-navy focus:outline-none"
                    />
                  </div>
                  <PasswordInput
                    value={password}
                    onChange={setPassword}
                    required
                    autoComplete="current-password"
                    label="密码"
                  />
                </>
              )}
              {errorBlock}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded bg-navy-fill px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-fill-strong disabled:opacity-50 max-lg:rounded-control"
              >
                {loading ? '登录中…' : '登录'}
              </button>
            </form>

            <div className="mt-4 flex items-center justify-between text-sm text-fg-muted">
              <span>
                还没有账号？{' '}
                <button
                  type="button"
                  onClick={() => onModeChange('register')}
                  className="text-navy hover:underline"
                >
                  注册
                </button>
              </span>
              <Link
                to="/forgot-password"
                onClick={onClose}
                className="text-navy hover:underline"
              >
                忘记密码？
              </Link>
            </div>
          </div>

          {isDesktop && (
            <>
              <div className="w-px self-stretch bg-line" aria-hidden="true" />
              <div>
                <ScanLoginPanel active={open && isLogin && isDesktop} onSuccess={onSuccess} />
              </div>
            </>
          )}
        </div>
      ) : (
        <div>
          <form className="space-y-4" onSubmit={submit}>
            <div>
              <label className="block text-sm font-medium text-fg-muted">手机号</label>
              <input
                type="tel"
                required
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="mt-1 w-full rounded border border-line px-3 py-2 text-sm focus:border-navy focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-muted">验证码</label>
              <div className="mt-1 flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="6 位验证码"
                  className="w-full rounded border border-line px-3 py-2 text-sm focus:border-navy focus:outline-none"
                />
                <SmsCodeButton phone={phone} purpose="register" onError={setError} />
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm text-fg-muted">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-line accent-navy"
              />
              <span>
                我已阅读并接受
                <Link to="/privacy" target="_blank" className="text-navy hover:underline">
                  《隐私政策》
                </Link>
              </span>
            </label>

            <div>
              <button
                type="button"
                onClick={() => setShowOptional((v) => !v)}
                className="text-sm text-navy hover:underline"
              >
                {showOptional ? '收起' : '设置密码 / 补充邮箱（可选，可跳过）'}
              </button>
              {showOptional && (
                <div className="mt-3 space-y-4">
                  <PasswordInput
                    value={password}
                    onChange={setPassword}
                    autoComplete="new-password"
                    showStrength
                    label="密码（可选，至少 8 位，含字母与数字）"
                  />
                  <div>
                    <label className="block text-sm font-medium text-fg-muted">邮箱（可选）</label>
                    <input
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 w-full rounded border border-line px-3 py-2 text-sm focus:border-navy focus:outline-none"
                    />
                  </div>
                </div>
              )}
            </div>

            {errorBlock}
            <button
              type="submit"
              disabled={loading || !agreed}
              className="w-full rounded bg-navy-fill px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-fill-strong disabled:opacity-50 max-lg:rounded-control"
            >
              {loading ? '注册中…' : '注册'}
            </button>
            <p className="text-center text-xs leading-relaxed text-fg-faint">
              为防范盗版倒卖与保障账户安全，本站会记录注册及下载时的设备特征与网络信息。
            </p>
          </form>

          <div className="mt-4 flex items-center justify-between text-sm text-fg-muted">
            <span>
              已有账号？{' '}
              <button
                type="button"
                onClick={() => onModeChange('login')}
                className="text-navy hover:underline"
              >
                登录
              </button>
            </span>
          </div>
        </div>
      )}
    </Modal>
  );
}
